import React from "react";
import type { FluentProviderProps, BrandVariants, Theme } from "@fluentui/react-components";
import { createDOMRenderer, RendererProvider, createDarkTheme, createLightTheme, } from "@fluentui/react-components";
import { PortalCompatProvider } from "@fluentui/react-portal-compat";
import { HeadlessFluentProvider } from "@fluentui-contrib/react-headless-provider";
import type { IPalette } from "@fluentui/style-utilities";
import { isCurrentCultureRightToLeft } from "owa-localize";
import { getIsDarkTheme } from "owa-fabric-theme";
import { getPaletteAsRawColors, getBrandVariants } from "owa-theme";
import { observer } from "owa-mobx-react";
import type { ColorInput } from "@ctrl/tinycolor";
import { TinyColor } from "@ctrl/tinycolor";
import { FluentThemeWrapper } from "./FluentThemeWrapper";
import { FluentComponentsThemeContext } from "./FluentComponentsThemeContext";
import { calendarMacFontsCapability } from "owa-capabilities-definitions/lib/calendarMacFontsCapability";
import { isCapabilityEnabled } from "owa-capabilities";
import { fontFamilyForMac } from "./fontFamilyForMac";

const macFontStyleV9 = {
    fontFamilyBase: fontFamilyForMac,
} as const;

function mixColors(colorA: ColorInput, colorB: ColorInput, amount?: number) {
    return new TinyColor(colorA).mix(new TinyColor(colorB), amount).toHexString();
}

export function mixByPairs(colors: IPalette): BrandVariants {
    return {
        10: colors.themeDarker,
        20: mixColors(colors.themeDarker, colors.themeDark),
        30: colors.themeDark,
        40: mixColors(colors.themeDark, colors.themeDarkAlt),
        50: colors.themeDarkAlt,
        60: mixColors(colors.themeDarkAlt, colors.themePrimary),
        70: colors.themePrimary,
        80: mixColors(colors.themePrimary, colors.themeSecondary),
        90: colors.themeSecondary,
        100: mixColors(colors.themeSecondary, colors.themeTertiary),
        110: colors.themeTertiary,
        120: mixColors(colors.themeTertiary, colors.themeLight),
        130: colors.themeLight,
        140: mixColors(colors.themeLight, colors.themeLighter),
        150: colors.themeLighter,
        160: mixColors(colors.themeLighter, colors.themeLighterAlt),
    };
}

export function useV9BrandTheme(isDarkMode: boolean = getIsDarkTheme()): Theme {
    const palette = getPaletteAsRawColors(undefined, isDarkMode) as IPalette;
    const fontStyle = isCapabilityEnabled(calendarMacFontsCapability) ? macFontStyleV9 : {};
    
    return React.useMemo<Theme>(() => {
        const colorRamp = getBrandVariants();
        const brandVariants = colorRamp ? colorRamp : mixByPairs(palette);
        return {
            ...(isDarkMode ? createDarkTheme : createLightTheme)(brandVariants),
            ...fontStyle,
        };
    }, [palette, isDarkMode]);
}

export const FluentContext: React.FunctionComponent<Pick<FluentProviderProps, "children" | "theme" | "dir" | "targetDocument">> = observer(function FluentContext({ dir = isCurrentCultureRightToLeft() ? "rtl" : "ltr", targetDocument = window.document, children, }: Pick<FluentProviderProps, "theme" | "dir" | "targetDocument" | "children">) {
    const theme = useV9BrandTheme();
    
    const renderer = React.useMemo(() => {
        const insertionPoint = targetDocument.head.querySelector("title") ?? undefined;
        (targetDocument as any).fluentRendererProvider =
            (targetDocument as any).fluentRendererProvider ??
                createDOMRenderer(targetDocument, { insertionPoint });
        return (targetDocument as any).fluentRendererProvider;
    }, [targetDocument]);
    
    return (<RendererProvider renderer={renderer} targetDocument={targetDocument}>
            <FluentComponentsThemeContext>
                <FluentThemeWrapper theme={theme} targetDocument={targetDocument}>
                    <HeadlessFluentProvider dir={dir} targetDocument={targetDocument}>
                        <PortalCompatProvider>{children}</PortalCompatProvider>
                    </HeadlessFluentProvider>
                </FluentThemeWrapper>
            </FluentComponentsThemeContext>
        </RendererProvider>);
}, "FluentContext");
